import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InputMask from 'react-input-mask';
import cn from 'classnames';
import {
  Select,
  Option,
  Button,
  Input,
  Checkbox,
  Loader,
  getSpecificationsForGroup,
} from '@azgaz/azgaz-ui';
import { useSelector as useSelectorConnect, selectors } from '@lib/azgaz-ui/connect';

import { config, Retargeting } from '@/utils';
import { FORM_IDS } from '@/constants';
import useFormHandlers from '@/hooks/useFormHandlers';
import { getBook, getCarBookOpts, getCarDealerOffices } from '@/selectors';
import { fields, actions } from '../../store/book';

import Title from '../Title';
import { TOffice } from '@/apps/appCreator/components/CarOfficeCard';

import style from './style.module.scss';

type CarBookFormProps = {
  form: any;
};

const CarBookForm: FC<CarBookFormProps> = ({
  form: {
    getFieldDecorator,
    setFieldsValue,
    getFieldsValue,
    isFieldTouched,
    resetFields,
  },
  form,
}) => {
  const retargetingSent = useRef(false);
  const car = useSelectorConnect(selectors.getCar);
  const carBookOpts = useSelector(getCarBookOpts);
  const carPrice = car?.price?.value?.[0][0];
  const carPriceDiscounted =
    carPrice -
    carBookOpts.discountTradeInCarPrice -
    carBookOpts.discountTradeIn -
    carBookOpts.discountSuzukiFinance -
    carBookOpts.discountMedical;

  let dcOffices: TOffice[] | undefined;
  let carDescription = '';
  if (car) {
    const specifications = getSpecificationsForGroup(
      car!.specification,
      'main',
      ['engine', 'transmission', 'drive']
    );

    const { grade, model } = car;
    const { engine, transmission, drive } = specifications;
    const colorName = car?.color?.name || '';
    carDescription = `${model} ${engine} ${transmission} ${drive}, ${grade}, ${colorName}`;

    const { offices } = getCarDealerOffices(car);
    dcOffices = offices;
  }

  const { getError, onSubmit } = useFormHandlers({
    form,
    submitAction: (formFields: any) =>
      actions.booking({
        ...formFields,
        car,
        carDescription,
        VIN: car?.vin,
        city: car?.main_office?.city,
        carPrice: carPrice.toString(), // передаем поле как string
        carPriceDiscounted:
          carPriceDiscounted < 0 ? '0' : carPriceDiscounted.toString(), // передаем поле как string
        ...carBookOpts,
        discountTradeInCarPrice: carBookOpts.discountTradeInCarPrice.toString(), // передаем поле как string
      }),
  });

  const setDefaultDc = useCallback(() => {
    setFieldsValue({
      [fields.dealer]:
        dcOffices?.[0].dealerCenterStaticId ?? dcOffices?.[0].dealerStaticId,
    });
  }, []);

  const dispatch = useDispatch();
  const reset = useCallback(() => {
    dispatch(actions.resetForm());
    setDefaultDc();
  }, []);

  const { loading, booked, error } = useSelector(getBook);

  const fieldValues = getFieldsValue();

  const isJimny = useMemo(() => car?.model.toUpperCase() === 'JIMNY', [
    car?.model,
  ]);

  const initValues = useRef<any>(null);

  const dirtyFields = useMemo(() => {
    if (!initValues.current) return false;
    const formFields = [fields.name, fields.phone];
    return formFields.some(field => {
      return initValues.current?.[field] !== fieldValues?.[field];
    });
  }, [fieldValues]);

  const isExistValues = useMemo(() => {
    return (
      fieldValues?.name?.length > 0 ||
      fieldValues?.phone?.substring(3).length > 0
    );
  }, [fieldValues]);

  useEffect(() => {
    if (dirtyFields && isExistValues && !retargetingSent.current && !!car) {
      const key = isJimny ? 'EXC10008' : 'EXC10006';
      Retargeting.onReady(['6011742f98657b070ebd92d8', key]);
      retargetingSent.current = true;
    }
  }, [dirtyFields, isExistValues, isJimny, car]);

  useEffect(() => {
    setDefaultDc();
    initValues.current = getFieldsValue();
  }, []);

  return (
    <div className={cn(style.wrap, { [style.booked]: booked })}>
      <form id={FORM_IDS.bookingCar} onSubmit={onSubmit} noValidate>
        <div className="row">
          <div className="col-lg-6 mb-4">
            {getFieldDecorator(fields.name, {
              rules: [
                {
                  required: true,
                  message: 'Обязательное поле',
                },
              ],
            })(<Input title="Имя*" hint={getError(fields.name)} />)}
          </div>
          <div className="col-lg-6 mb-4">
            {getFieldDecorator(fields.phone, {
              rules: [
                { required: true, message: 'Укажите телефон' },
                {
                  type: 'string',
                  pattern: /^\+\d \d\d\d \d\d\d-\d\d-\d\d$/,
                  message: 'Укажите телефон в формате +7 123 456-78-90',
                },
              ],
            })(
              <InputMask
                mask="+7 999 999-99-99"
                placeholder="+7 123 456-78-90"
                maskChar=""
                type="tel"
              >
                {(props: any) => (
                  <Input
                    title="Телефон*"
                    type="tel"
                    hint={getError(fields.phone)}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              </InputMask>
            )}
          </div>
        </div>
        <div className="row">
          <div
            className={cn('col-lg-6', 'mb-4', {
              'd-none': !dcOffices || dcOffices.length === 1,
            })}
          >
            {getFieldDecorator(fields.dealer, {
              rules: [
                {
                  required: true,
                  message: 'Обязательное поле',
                },
              ],
            })(
              <Select
                title="Дилерский центр*"
                optionLabelProp="children"
                showSearch={false}
                hint={getError(fields.dealer)}
              >
                {dcOffices?.map(
                  ({ dealerCenterStaticId, dealerStaticId, name }) => (
                    <Option key={dealerCenterStaticId ?? dealerStaticId}>
                      {name}
                    </Option>
                  )
                )}
              </Select>
            )}
          </div>
        </div>
        <div className="row mt-2 mt-lg-4 align-items-center">
          <div className="col-lg mb-4">
            {getFieldDecorator(fields.term, {
              rules: [
                {
                  validator: (options: object, value: boolean) => value,
                  required: true,
                  message:
                    'Нужно ваше согласие на обработку персональных данных',
                },
              ],
            })(
              <Checkbox hint={getError(fields.term)}>
                <span className="ml-n3 mt-n2">
                  <span className="ml-3 mt-2 d-inline-block">
                    Я согласен на обработку персональных данных
                  </span>
                  <a
                    className="ml-3 mt-2 d-inline-block"
                    href={config.get('termsLink')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Смотреть правила
                  </a>
                </span>
              </Checkbox>
            )}
          </div>
          <div className="col-md-6 col-lg-4 mb-4 d-flex justify-content-lg-end flex-wrap">
            <Button type="submit" wide disabled={loading}>
              <span className={style.submitBtn}>
                отправить заявку
                {loading && (
                  <Loader
                    color="white"
                    size={3}
                    className={style.submitBtnLoader}
                  />
                )}
              </span>
            </Button>
            {error && <div className={style.submitError}>{error}</div>}
          </div>
        </div>
      </form>
      <div className={style.successMsg}>
        <Title tag="h2" className={style.successTitle}>
          Ваша заявка успешно отправлена!
        </Title>
        <button type="button" className={style.successRepeat} onClick={reset}>
          Заполнить повторно
        </button>
      </div>
    </div>
  );
};

export default CarBookForm;
